import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { LayoutDashboard, Plus, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { BOARD } from "@/constants/testIds";
import api from "@/lib/api";

export function AddToDashboardDialog({ open, onOpenChange, ctx, workspaceId }) {
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!open || !workspaceId) return;
    setLoading(true);
    try {
      const { data } = await api.get("/dashboards", { params: { workspace_id: workspaceId } });
      setBoards(data);
    } finally {
      setLoading(false);
    }
  }, [open, workspaceId]);

  useEffect(() => { setNewName(""); load(); }, [load]);

  const addWidget = async (dashboardId) => {
    if (!ctx || !ctx.sql) {
      toast.error("This answer can't be added (no query available).");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/dashboards/${dashboardId}/widgets`, {
        title: (ctx.title || "Visualization").slice(0, 160),
        source_id: ctx.sourceId || null,
        sql: ctx.sql,
        chart: ctx.chart,
        question: ctx.question || null,
      });
      toast.success("Added to dashboard.", {
        action: { label: "Open", onClick: () => { window.location.href = `/boards/${dashboardId}`; } },
      });
      onOpenChange(false);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Could not add to dashboard.");
    } finally {
      setBusy(false);
    }
  };

  const createAndAdd = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/dashboards", {
        workspace_id: workspaceId,
        name: newName.trim() || "Untitled dashboard",
      });
      await addWidget(data.id);
    } catch {
      toast.error("Could not create dashboard.");
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={BOARD.addDialog} className="font-mono sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add to dashboard</DialogTitle>
          <DialogDescription className="truncate">Pin “{ctx?.title}” to a dashboard.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">Your dashboards</p>
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            ) : boards.length ? (
              <div className="max-h-52 space-y-2 overflow-y-auto">
                {boards.map((b) => (
                  <button
                    key={b.id}
                    data-testid={BOARD.addPick}
                    disabled={busy}
                    onClick={() => addWidget(b.id)}
                    className="flex w-full items-center justify-between rounded-lg border border-border/60 px-3 py-2 text-left text-sm transition-colors hover:border-accent/50 disabled:opacity-50"
                  >
                    <span className="flex items-center gap-2 truncate">
                      <LayoutDashboard className="h-4 w-4 shrink-0 text-accent" />
                      {b.name}
                    </span>
                    <span className="shrink-0 text-xs text-muted-foreground">{b.widget_count ?? 0} viz</span>
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">No dashboards yet — create one below.</p>
            )}
          </div>

          <div className="space-y-2 border-t border-border/50 pt-4">
            <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">Or create new</p>
            <div className="flex gap-2">
              <Input
                data-testid={BOARD.addNewName}
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Dashboard name"
                className="text-sm"
              />
              <Button
                data-testid={BOARD.addSubmit}
                disabled={busy || !workspaceId}
                onClick={createAndAdd}
                className="shrink-0 rounded-full bg-accent text-accent-foreground hover:bg-accent/90"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
